/**
 * User Preference Utilities
 * Handles localStorage-based persistence for user preferences
 */

const STORAGE_KEYS = {
	CALENDAR_DAYS_VIEW: 'calendar_days_view',
	CALENDAR_SELECTED_EMPLOYEES: 'calendar_selected_employees',
	MOBILE_CALENDAR_VIEW_MODE: 'mobile_calendar_view_mode',
}

/**
 * Safely get an item from localStorage
 * @param {string} key - The storage key
 * @returns {string|null} The stored value or null
 */
const getStorageItem = (key) => {
	try {
		return localStorage.getItem(key)
	} catch (error) {
		console.warn('Failed to read from localStorage:', error)
		return null
	}
}

/**
 * Safely set an item in localStorage
 * @param {string} key - The storage key
 * @param {string} value - The value to store
 */
const setStorageItem = (key, value) => {
	try {
		localStorage.setItem(key, value)
	} catch (error) {
		console.warn('Failed to write to localStorage:', error)
	}
}

/**
 * Get the saved calendar days view preference
 * @param {number|string} defaultValue - Default value if no preference is saved
 * @returns {number|string} The saved days view (1, 3, 5, 7, or 'month') or the default
 */
export const getCalendarDaysView = (defaultValue = 5) => {
	const stored = getStorageItem(STORAGE_KEYS.CALENDAR_DAYS_VIEW)
	if (stored !== null) {
		// Check for 'month' string value first
		if (stored === 'month') {
			return 'month'
		}
		const parsed = parseInt(stored, 10)
		// Validate it's a valid numeric option
		if ([1, 3, 5, 7].includes(parsed)) {
			return parsed
		}
	}
	return defaultValue
}

/**
 * Save the calendar days view preference
 * @param {number|string} daysView - The days view to save (1, 3, 5, 7, or 'month')
 */
export const setCalendarDaysView = (daysView) => {
	setStorageItem(STORAGE_KEYS.CALENDAR_DAYS_VIEW, String(daysView))
}

/**
 * Get the saved selected employee IDs for the calendar
 * @param {string} merchantId - The merchant ID to scope the preference
 * @returns {string[]} Array of selected employee IDs, or empty array if none saved
 */
export const getCalendarSelectedEmployeeIds = (merchantId) => {
	if (!merchantId) return []

	const key = `${STORAGE_KEYS.CALENDAR_SELECTED_EMPLOYEES}_${merchantId}`
	const stored = getStorageItem(key)

	if (stored !== null) {
		try {
			const parsed = JSON.parse(stored)
			if (Array.isArray(parsed)) {
				return parsed
			}
		} catch (error) {
			console.warn('Failed to parse selected employees:', error)
		}
	}
	return []
}

/**
 * Save the selected employee IDs for the calendar
 * @param {string} merchantId - The merchant ID to scope the preference
 * @param {string[]} employeeIds - Array of selected employee IDs
 */
export const setCalendarSelectedEmployeeIds = (merchantId, employeeIds) => {
	if (!merchantId) return

	const key = `${STORAGE_KEYS.CALENDAR_SELECTED_EMPLOYEES}_${merchantId}`
	setStorageItem(key, JSON.stringify(employeeIds))
}

// Legacy mobile view mode values from before the "Mobile · calendar" redesign
const LEGACY_MOBILE_VIEW_MODES = { daily: 'list', monthly: 'month' }

/**
 * Get the saved mobile calendar view mode preference
 * @param {string} defaultValue - Default value if no preference is saved
 * @returns {string} The saved view mode ('list', 'grid' or 'month') or the default
 */
export const getMobileCalendarViewMode = (defaultValue = 'list') => {
	const stored = getStorageItem(STORAGE_KEYS.MOBILE_CALENDAR_VIEW_MODE)
	if (stored !== null) {
		if (['list', 'grid', 'month'].includes(stored)) return stored
		if (LEGACY_MOBILE_VIEW_MODES[stored]) return LEGACY_MOBILE_VIEW_MODES[stored]
	}
	return defaultValue
}

/**
 * Save the mobile calendar view mode preference
 * @param {string} viewMode - The view mode to save ('list', 'grid' or 'month')
 */
export const setMobileCalendarViewMode = (viewMode) => {
	if (['list', 'grid', 'month'].includes(viewMode)) {
		setStorageItem(STORAGE_KEYS.MOBILE_CALENDAR_VIEW_MODE, viewMode)
	}
}

/**
 * Clear all calendar preferences
 * @param {string} merchantId - The merchant ID to clear preferences for
 */
export const clearCalendarPreferences = (merchantId) => {
	try {
		localStorage.removeItem(STORAGE_KEYS.CALENDAR_DAYS_VIEW)
		localStorage.removeItem(STORAGE_KEYS.MOBILE_CALENDAR_VIEW_MODE)
		if (merchantId) {
			localStorage.removeItem(`${STORAGE_KEYS.CALENDAR_SELECTED_EMPLOYEES}_${merchantId}`)
		}
	} catch (error) {
		console.warn('Failed to clear calendar preferences:', error)
	}
}

